// isocube.jsx - slowly rotating isometric cube that morphs between
// a plaintext ".py" cube (visible faint glyphs) and a sealed ".pyd"
// binary cube (solid faces, faint binary texture).
//
// Driven by a progress prop (0..1). 0 = source, 1 = protected.

const { useState, useEffect } = React;

function IsoCube({ progress = 0, size = 360 }) {
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const actualSize = Math.min(size, Math.max(220, vw - 48));
  const p = Math.max(0, Math.min(1, progress));
  const rot = 20 + p * 12; // subtle camera tilt

  return (
    <div style={{
      width: actualSize, height: actualSize, position: 'relative',
      perspective: 1800, perspectiveOrigin: '50% 40%',
      margin: '0 auto',
      maxWidth: '100%',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        transformStyle: 'preserve-3d',
        transform: `rotateX(${rot}deg) rotateY(${-35 + p * 20}deg) rotateZ(${p * 4}deg)`,
        transition: 'transform 1.2s cubic-bezier(.2,.7,.2,1)',
        animation: 'iso-drift 18s ease-in-out infinite',
      }}>
        {/* 6 faces */}
        <Face face="top"    size={actualSize} progress={p} />
        <Face face="front"  size={actualSize} progress={p} />
        <Face face="right"  size={actualSize} progress={p} />
        <Face face="back"   size={actualSize} progress={p} />
        <Face face="left"   size={actualSize} progress={p} />
        <Face face="bottom" size={actualSize} progress={p} />
      </div>
      {/* ambient glow beneath */}
      <div style={{
        position: 'absolute', left: '10%', right: '10%', bottom: '8%',
        height: 60, borderRadius: '50%',
        background: `radial-gradient(ellipse, rgba(59,130,246,${0.35 + p * 0.4}) 0%, transparent 70%)`,
        filter: 'blur(20px)',
        transition: 'background 1.2s ease',
      }} />
      <style>{`
        @keyframes iso-drift {
          0%,100% { translate: 0 0; }
          50%     { translate: 0 -14px; }
        }
      `}</style>
    </div>
  );
}

function Face({ face, size, progress }) {
  const half = size / 2;
  const transforms = {
    top:    `rotateX(90deg) translateZ(${half}px)`,
    bottom: `rotateX(-90deg) translateZ(${half}px)`,
    front:  `translateZ(${half}px)`,
    back:   `rotateY(180deg) translateZ(${half}px)`,
    left:   `rotateY(-90deg) translateZ(${half}px)`,
    right:  `rotateY(90deg) translateZ(${half}px)`,
  };
  // source palette (cool gray with faint code) -> protected (deep blue sealed)
  const srcBg = 'linear-gradient(135deg, rgba(245,241,232,0.04), rgba(245,241,232,0.01))';
  const protBg = 'linear-gradient(135deg, rgba(59,130,246,0.22), rgba(29,78,216,0.08))';
  const borderSrc = 'rgba(245,241,232,0.14)';
  const borderProt = 'rgba(147,197,253,0.4)';
  return (
    <div style={{
      position: 'absolute', inset: 0,
      transform: transforms[face],
      background: `linear-gradient(135deg, rgba(10,10,15,0.9), rgba(10,10,15,0.6))`,
      border: `1px solid ${mix(borderSrc, borderProt, progress)}`,
      boxShadow: `inset 0 0 60px rgba(59,130,246,${progress * 0.25})`,
      overflow: 'hidden', backfaceVisibility: 'hidden',
    }}>
      {/* source layer: faint code */}
      <FaceContent kind="source" opacity={1 - progress} face={face} />
      {/* protected layer: binary / sealed */}
      <FaceContent kind="protected" opacity={progress} face={face} />
      {/* sheen */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `linear-gradient(${face === 'top' ? '0deg' : '135deg'}, ${mix(srcBg, protBg, progress)})`,
        mixBlendMode: 'screen',
      }} />
      {/* scan line only on front when transitioning */}
      {face === 'front' && (
        <div style={{
          position: 'absolute', left: 0, right: 0,
          top: `${progress * 100}%`,
          height: 2, background: 'linear-gradient(90deg, transparent, rgba(147,197,253,0.8), transparent)',
          boxShadow: '0 0 18px rgba(147,197,253,0.9)',
          opacity: progress > 0.02 && progress < 0.98 ? 1 : 0,
          transition: 'top 0.3s linear, opacity 0.3s ease',
        }} />
      )}
    </div>
  );
}

function FaceContent({ kind, opacity, face }) {
  if (opacity < 0.02) return null;
  if (kind === 'source') {
    return (
      <pre style={{
        position: 'absolute', inset: 0, margin: 0, padding: 12,
        fontFamily: 'JetBrains Mono, monospace', fontSize: 9, lineHeight: 1.5,
        color: 'rgba(245,241,232,0.35)', opacity,
        whiteSpace: 'pre', overflow: 'hidden',
        transition: 'opacity .8s ease',
      }}>
{`def validate(key):
  salt = "internal"
  h = sha256(
    key + salt
  ).hexdigest()
  return verify(h)

SECRET_URL = "api/v2"
TOKEN = "f9a2b1"
if user.valid:
  run_logic()`}
      </pre>
    );
  }
  // protected: binary texture + opcode dots
  return (
    <div style={{
      position: 'absolute', inset: 0, opacity, overflow: 'hidden',
      transition: 'opacity .8s ease',
    }}>
      <pre style={{
        margin: 0, padding: 12,
        fontFamily: 'JetBrains Mono, monospace', fontSize: 7.5, lineHeight: 1.4,
        color: 'rgba(147,197,253,0.55)', whiteSpace: 'pre', overflow: 'hidden',
      }}>
{`0100 1101 1010 0011
1110 0100 0111 1001
0010 1011 1100 0101
1001 0110 0011 1010
0111 1101 0100 0010
1010 0001 1110 1101
0110 1100 0011 0101`}
      </pre>
      <div style={{
        position: 'absolute', bottom: 8, left: 10, right: 10,
        display: 'flex', justifyContent: 'space-between',
        fontFamily: 'JetBrains Mono, monospace', fontSize: 8,
        color: 'rgba(147,197,253,0.8)', letterSpacing: 1,
      }}>
        <span>vm.sealed</span><span>·</span><span>.pyd</span>
      </div>
    </div>
  );
}

function mix(a, b, t) {
  return t < 0.5 ? a : b;
}

window.IsoCube = IsoCube;
